import { Controller } from "@hotwired/stimulus"

// Drives the streaming-import markup on both shapes of mount this app has:
//
//   - csvbox mounts (admin/products/index, the three admin/gifts/bulk_management modals) declare
//     Stimulus targets and are driven by a sibling csvbox controller, which dispatches
//     "csvbox:imported" once the hosted widget resolves its import id.
//   - rails-ujs mounts (admin/snapshots/index, suppliers/fulfillments/index) carry no markup
//     beyond the data-controller attribute. A plain `remote: true` form fires "ajax:complete"
//     through jQuery, and the elements are found by class.
//
// Every element lookup below prefers the target and falls back to the class selector, so a mount
// only has to declare whichever it already uses. A mount missing an element yields null or an
// empty NodeList, which is the same safe no-op both shapes relied on before.
export default class extends Controller {
  static get targets() {
    return ["errorContainer", "completeContainer", "form", "importTable", "importClose"];
  }

  static get values() {
    return { redirectUrlTemplate: String };
  }

  connect() {
    const $element = $(this.element)
    if (this.hasFormTarget) {
      this.$form = $(this.formTarget)
    } else {
      const scopedForm = $element.find(".modal-body form")
      this.$form = scopedForm.length > 0 ? scopedForm : $element.find("form")
    }
    this.callback = null
    this.subscription = null
    // csvbox mounts reload once the stream finishes, the way they did before this controller
    // absorbed them; rails-ujs mounts keep showing their completed table and run whatever
    // callback the caller handed to "ajax:complete" instead.
    this.reloadOnComplete = false

    // Bound here (not a class-body field - jshint's esversion 11 doesn't parse those) so the same
    // function reference is passed to both .on() and .off(), and `this` inside it is the
    // controller instance the way jQuery invokes it.
    this.handleAjaxComplete = (event, data, status) => {
      if (!data.responseText) {
        this.handleError(status)
        return
      }

      if (data.callback) {
        this.callback = data.callback
      }

      this.reloadOnComplete = false
      this.handleParsedResponse(JSON.parse(data.responseText))
    }
    this.$form.on("ajax:complete.importChannel", this.handleAjaxComplete)
  }

  disconnect() {
    this.$form.off("ajax:complete.importChannel", this.handleAjaxComplete)
    this.stopStreaming()
  }

  // A mount can go away mid-import (Turbo navigation, or the modal being torn out), and the
  // channel keeps pushing rows at a receiver whose DOM is gone. Guarded because a final message
  // and a disconnect can both land.
  stopStreaming() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  eachIn(selector, callback) {
    this.element.querySelectorAll(selector).forEach(callback)
  }

  importTableElements() {
    if (this.hasImportTableTarget) { return [this.importTableTarget] }
    return Array.from(this.element.querySelectorAll("table.table-imports"))
  }

  completeContainerElements() {
    if (this.hasCompleteContainerTarget) { return [this.completeContainerTarget] }
    return Array.from(this.element.querySelectorAll(".imports-complete"))
  }

  handleError(errorText, el) {
    if (el) { $(el).removeAttr("disabled") }

    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = errorText
    } else {
      this.eachIn(".error", (node) => { node.textContent = errorText })
    }
  }

  submitCSVForm(event) {
    event.preventDefault()
    const btn = event.target
    $(btn).attr("disabled", "disabled")

    fetch(this.$form.get(0).action, {
      "credentials": "same-origin",
      "method": "POST",
      "body": new FormData(this.$form.get(0))
    }).
      then(response => response.text()).
      then(html => this.handleParsedResponse(JSON.parse(html), btn))
  }

  // Entry point for the csvbox controllers, which POST the import themselves and dispatch
  // "csvbox:imported" with the parsed JSON response.
  handleExternalImport(event) {
    this.reloadOnComplete = true
    this.handleParsedResponse(event.detail, event.target)
  }

  handleParsedResponse(json, btn) {
    if (json.status !== "ok") {
      this.handleError(json.error, btn)
      return
    }

    // Some imports (e.g. bulk gift import/update/cancel) don't stream row-by-row progress over
    // ActionCable - they just validate asynchronously and review happens on a separate page, so
    // send the admin there instead of waiting on a stream message that will never arrive.
    if (this.hasRedirectUrlTemplateValue) {
      window.location.href = this.redirectUrlTemplateValue.replace("EXTERNAL_IMPORT_ID", json.importId)
      return
    }

    this.beginStream(json.importId, json.channelName)
  }

  beginStream(importId, channelName) {
    if (this.hasImportCloseTarget) {
      this.importCloseTarget.classList.add("hidden")
    } else {
      this.eachIn(".close", (el) => el.classList.add("hidden"))
    }
    this.$form.addClass("hidden")
    this.importTableElements().forEach((el) => el.classList.remove("hidden"))
    // Only the rails-ujs mounts have a separate loader; on the csvbox mounts the element above is
    // already the streaming loader.
    this.eachIn(".loader", (el) => el.classList.remove("hidden"))

    const receiveHandler = (data) => {
      // `data.html` is a row partial rendered by our own ImportChannel on the server, never
      // client-supplied text, so appending it as markup is the intended contract here. It stays
      // on jQuery's `.append` rather than `insertAdjacentHTML` because the two differ on whether
      // embedded <script> runs, and the partials are not audited for that.
      if (data.event === "import") {
        this.importTableElements().forEach((el) => $(el).append(data.html))
      } else {
        this.eachIn(".loader", (el) => el.classList.add("hidden"))
        this.completeContainerElements().forEach((el) => $(el).append(data.html))
        this.stopStreaming()

        if (this.reloadOnComplete) {
          window.location.reload()
        } else if (this.callback) {
          this.callback()
        }
      }
    }

    this.subscription = App.cable.subscriptions.create({ channel: channelName, id: importId }, {
      received: receiveHandler
    })
  }
}
